import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SEOWrapper } from "@/components/SEOWrapper";
import { DiagnosisForm } from "@/components/DiagnosisForm";
import { AiSeoRecommendations } from "@/components/AiSeoRecommendations";
import { RocSignalLabel } from "@/components/roc/RocSignalLabel";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { usePageSeo } from "@/hooks/usePageSeo";
import { esRoutes, enRoutes } from "@/utils/routes";

/**
 * ROC STRATEGIC INTAKE — /diagnostico-seo-gratis · /en/free-seo-diagnosis
 * P5.10.3: oferta owner-verified = reunión inicial gratuita de 30 minutos.
 * No promete auditoría, reporte, plan de acción ni hallazgos garantizados.
 */
const DiagnosticoSeoPage = () => {
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const routes = language === "es" ? esRoutes : enRoutes;

  const content = {
    es: {
      meta: {
        title: "Reunión inicial gratuita de 30 minutos | Luis ROC",
        description:
          "Reunión inicial gratuita de 30 minutos para revisar el contexto de tu negocio y determinar posibles siguientes pasos en SEO, Search Growth o Google Ads.",
        keywords: [
          "reunión inicial gratuita 30 minutos",
          "consultoría SEO B2B",
          "consultor SEO Houston",
        ],
      },
      hero: {
        signal: "ROC Strategic Intake",
        h1: "Reunión inicial gratuita de 30 minutos",
        lead:
          "Una conversación de 30 minutos para entender tu negocio, revisar el contexto de tu presencia en buscadores y determinar posibles siguientes pasos. No es una auditoría, un reporte ni un plan de acción: es el punto de entrada para saber qué tipo de trabajo tendría sentido.",
        cta: "Solicitar la reunión",
        note: "30 minutos · Sin compromiso · Español o inglés",
      },
      flow: ["Necesidad", "Contexto", "Prioridad", "Siguiente paso"],
      sections: {
        context: {
          index: "01",
          label: "Contexto",
          title: "Para qué sirve esta reunión",
          body:
            "La mayoría de las empresas B2B llegan con la misma pregunta formulada de distintas maneras: el sitio no genera consultas, la inversión en marketing no se refleja en pipeline, o hay un cambio de mercado en puerta. En 30 minutos podemos ordenar esa situación y decidir si hay un problema de SEO técnico, de arquitectura, de contenido, de demanda pagada o de medición.",
          points: [
            "Entender el negocio, el ciclo de venta y quién decide la compra.",
            "Revisar el contexto competitivo y el mercado o mercados que atiendes.",
            "Determinar qué tipo de problema podría existir, sin asumir un diagnóstico cerrado.",
            "Acordar posibles siguientes pasos si tiene sentido continuar.",
          ],
        },
        review: {
          index: "02",
          label: "Qué revisamos",
          title: "Lo que puede observarse en 30 minutos",
          body:
            "Durante la conversación podemos mirar señales visibles de alto nivel. Son observaciones preliminares, no conclusiones técnicas: cualquier afirmación firme requiere trabajo de análisis posterior.",
          items: [
            {
              index: "A",
              title: "Situación de negocio",
              text: "Qué vendes, a quién, en qué mercados y qué está fallando en la generación de demanda.",
            },
            {
              index: "B",
              title: "Señales visibles",
              text: "Aspectos de alto nivel observables durante la llamada, sin auditoría ni herramientas de análisis profundo.",
            },
            {
              index: "C",
              title: "Tipo de problema",
              text: "Si la hipótesis apunta a lo técnico, la arquitectura, el contenido, la demanda pagada o la medición.",
            },
            {
              index: "D",
              title: "Encaje de servicio",
              text: "Si corresponde auditoría, SEO técnico, estrategia, Ads u otro servicio — o ninguno.",
            },
          ],
        },
        notIncluded: {
          index: "03",
          label: "Qué no es",
          title: "Lo que esta reunión no incluye",
          body:
            "Para evitar falsas expectativas, esta sesión gratuita no sustituye ningún servicio profesional.",
          items: [
            "No es una auditoría SEO completa.",
            "No es un análisis técnico profundo del sitio.",
            "No incluye keyword research ni análisis competitivo.",
            "No incluye un reporte posterior ni un plan de acción completo.",
            "No garantiza hallazgos específicos.",
            "No incluye implementación de correcciones.",
          ],
          note: "Todos esos trabajos existen como servicios con alcance definido por escrito.",
        },
        fit: {
          index: "04",
          label: "Encaje",
          title: "Para quién tiene sentido",
          idealTitle: "Tiene sentido si",
          idealItems: [
            "Eres una empresa B2B con tráfico estancado o en descenso.",
            "Has invertido en marketing sin ver reflejo en consultas cualificadas.",
            "Estás evaluando invertir en SEO, Search Growth o Google Ads.",
            "Operas en inglés y español, o en más de un mercado.",
          ],
          notTitle: "No tiene sentido si",
          notItems: [
            "Buscas una auditoría gratuita o un entregable sin contratación.",
            "Esperas posiciones o resultados garantizados.",
            "Tu proyecto no tiene presupuesto asignado a marketing.",
            "Ya trabajas con un consultor o agencia y no hay decisión de cambio.",
          ],
        },
        how: {
          index: "05",
          label: "Cómo funciona",
          title: "Cómo se usan los 30 minutos",
          steps: [
            {
              index: "01",
              title: "Formulario",
              text: "Compartes contexto básico: sitio, mercado y qué te preocupa. Sirve para llegar preparados.",
            },
            {
              index: "02",
              title: "Confirmación",
              text: "Te respondo durante horario laboral para coordinar la fecha y el canal (videollamada o presencial con cita previa).",
            },
            {
              index: "03",
              title: "Conversación",
              text: "30 minutos 1:1 con Luis ROC. Entendemos el negocio, revisamos el contexto y hablamos de prioridades.",
            },
            {
              index: "04",
              title: "Siguiente paso",
              text: "Cerramos la llamada con una lectura de qué tipo de trabajo podría corresponder. Si hay encaje, puedo proponer un alcance.",
            },
          ],
        },
        book: {
          index: "07",
          label: "Reserva",
          title: "Solicita la reunión",
          body: "Completa el formulario para darme contexto antes de nuestra reunión.",
        },
      },
      faq: {
        title: "Preguntas frecuentes",
        items: [
          {
            question: "¿Realmente es gratis?",
            answer:
              "Sí. La reunión inicial de 30 minutos es gratuita y sin compromiso. Sirve para entender qué necesita tu empresa y determinar posibles siguientes pasos; no sustituye una auditoría ni ningún servicio con alcance definido.",
          },
          {
            question: "¿Cuánto dura?",
            answer: "30 minutos, por videollamada o presencial con cita previa en el área de Houston, TX.",
          },
          {
            question: "¿Recibo un reporte después?",
            answer:
              "No. La reunión no incluye entregables. Si después de la conversación existe encaje, puedo proponerte un alcance de trabajo por escrito.",
          },
          {
            question: "¿Me van a vender algo?",
            answer:
              "Si identifico que puedo ayudarte y hay interés, te presentaré opciones. Si no hay encaje, te lo diré. No hay equipo de ventas ni secuencias automatizadas.",
          },
          {
            question: "¿Sirve para sitios nuevos?",
            answer:
              "Puede servir para ordenar prioridades, aunque con poco histórico las observaciones posibles son más limitadas.",
          },
        ],
      },
      explore: {
        title: "Antes de agendar, puedes revisar",
        links: [
          { to: routes.services, label: "Todos los servicios" },
          { to: routes.methodology, label: "ROC Search Growth Framework" },
          { to: routes.contact, label: "Contacto directo" },
        ],
      },
    },
    en: {
      meta: {
        title: "Free 30-Minute Initial Consultation | Luis ROC",
        description:
          "A free 30-minute initial consultation to review your business context and determine possible next steps in SEO, Search Growth or Google Ads.",
        keywords: [
          "free 30-minute initial consultation",
          "B2B SEO consulting",
          "Houston SEO consultant",
        ],
      },
      hero: {
        signal: "ROC Strategic Intake",
        h1: "Free 30-minute initial consultation",
        lead:
          "A 30-minute conversation to understand your business, review the context of your search presence and determine possible next steps. It is not an audit, a report or an action plan: it is the entry point to establish what kind of work would make sense.",
        cta: "Request the meeting",
        note: "30 minutes · No commitment · English or Spanish",
      },
      flow: ["Need", "Context", "Priority", "Next step"],
      sections: {
        context: {
          index: "01",
          label: "Context",
          title: "What this meeting is for",
          body:
            "Most B2B companies arrive with the same question phrased differently: the site doesn't generate inquiries, marketing spend isn't reflected in pipeline, or a market shift is coming. In 30 minutes we can organize that situation and decide whether the issue points to technical SEO, architecture, content, paid demand or measurement.",
          points: [
            "Understand the business, the sales cycle and who makes the buying decision.",
            "Review the competitive context and the market or markets you serve.",
            "Determine what type of problem may exist, without assuming a closed diagnosis.",
            "Agree on possible next steps if it makes sense to continue.",
          ],
        },
        review: {
          index: "02",
          label: "What we review",
          title: "What can be observed in 30 minutes",
          body:
            "During the conversation we can look at high-level visible signals. These are preliminary observations, not technical conclusions: any firm statement requires subsequent analysis work.",
          items: [
            {
              index: "A",
              title: "Business situation",
              text: "What you sell, to whom, in which markets and what is failing in demand generation.",
            },
            {
              index: "B",
              title: "Visible signals",
              text: "High-level aspects observable during the call, without an audit or deep analysis tooling.",
            },
            {
              index: "C",
              title: "Type of problem",
              text: "Whether the hypothesis points to technical issues, architecture, content, paid demand or measurement.",
            },
            {
              index: "D",
              title: "Service fit",
              text: "Whether an audit, technical SEO, strategy, Ads or another service applies — or none at all.",
            },
          ],
        },
        notIncluded: {
          index: "03",
          label: "What it is not",
          title: "What this meeting does not include",
          body:
            "To avoid false expectations, this free session does not replace any professional service.",
          items: [
            "It is not a complete SEO audit.",
            "It is not a deep technical analysis of the site.",
            "It does not include keyword research or competitive analysis.",
            "It does not include a follow-up report or a complete action plan.",
            "It does not guarantee specific findings.",
            "It does not include implementation of fixes.",
          ],
          note: "All of that work exists as services with a scope defined in writing.",
        },
        fit: {
          index: "04",
          label: "Fit",
          title: "Who this makes sense for",
          idealTitle: "It makes sense if",
          idealItems: [
            "You're a B2B company with stagnant or declining traffic.",
            "You've invested in marketing without seeing qualified inquiries.",
            "You're evaluating investment in SEO, Search Growth or Google Ads.",
            "You operate in English and Spanish, or across more than one market.",
          ],
          notTitle: "It doesn't make sense if",
          notItems: [
            "You're looking for a free audit or a deliverable without engagement.",
            "You expect guaranteed rankings or guaranteed results.",
            "Your project has no marketing budget allocated.",
            "You already work with a consultant or agency and there's no decision to change.",
          ],
        },
        how: {
          index: "05",
          label: "How it works",
          title: "How the 30 minutes work",
          steps: [
            {
              index: "01",
              title: "Form",
              text: "You share basic context: site, market and what concerns you. It helps us arrive prepared.",
            },
            {
              index: "02",
              title: "Confirmation",
              text: "I reply during business hours to arrange the date and channel (video call, or in person by appointment).",
            },
            {
              index: "03",
              title: "Conversation",
              text: "30 minutes 1:1 with Luis ROC. We understand the business, review context and talk priorities.",
            },
            {
              index: "04",
              title: "Next step",
              text: "We close with a reading of what type of work may apply. If there's a fit, I can propose a scope.",
            },
          ],
        },
        book: {
          index: "07",
          label: "Book",
          title: "Request the meeting",
          body: "Complete the form so I have context before our meeting.",
        },
      },
      faq: {
        title: "Frequently asked questions",
        items: [
          {
            question: "Is it really free?",
            answer:
              "Yes. The 30-minute initial meeting is free and carries no commitment. It exists to understand what your company needs and determine possible next steps; it does not replace an audit or any scoped service.",
          },
          {
            question: "How long is it?",
            answer: "30 minutes, by video call or in person by appointment in the Houston, TX area.",
          },
          {
            question: "Do I receive a report afterwards?",
            answer:
              "No. The meeting includes no deliverables. If there's a fit after the conversation, I can propose a scope of work in writing.",
          },
          {
            question: "Will I be sold something?",
            answer:
              "If I see that I can help and there's interest, I'll present options. If there's no fit, I'll say so. There's no sales team and no automated sequences.",
          },
          {
            question: "Does it work for new sites?",
            answer:
              "It can help order priorities, though with little history the possible observations are more limited.",
          },
        ],
      },
      explore: {
        title: "Before booking, you can review",
        links: [
          { to: routes.services, label: "All services" },
          { to: routes.methodology, label: "ROC Search Growth Framework" },
          { to: routes.contact, label: "Direct contact" },
        ],
      },
    },
  };

  const t = content[language];
  const s = t.sections;

  return (
    <SEOWrapper
      title={t.meta.title}
      description={t.meta.description}
      keywords={t.meta.keywords}
      url={url}
      esUrl={esUrl}
      enUrl={enUrl}
      language={language}
      pageType="website"
      structuredData={{
        type: "service",
        data: {
          name:
            language === "es"
              ? "Reunión inicial gratuita de 30 minutos"
              : "Free 30-minute initial consultation",
          description: t.meta.description,
          provider: "Luis ROC",
          areaServed: ["Texas", "Florida", "United States"],
          serviceType: "SEO consulting intake",
        },
      }}
      summary={
        language === "es"
          ? "Reunión inicial gratuita de 30 minutos con Luis ROC para revisar el contexto del negocio y determinar posibles siguientes pasos. No incluye auditoría, reporte ni plan de acción."
          : "Free 30-minute initial consultation with Luis ROC to review business context and determine possible next steps. No audit, report or action plan included."
      }
      keyPoints={
        language === "es"
          ? [
              "Reunión inicial gratuita de 30 minutos",
              "No es una auditoría ni un reporte gratuito",
              "Para empresas B2B en inglés y español",
              "Área de servicio: Texas y Florida",
            ]
          : [
              "Free 30-minute initial consultation",
              "Not a free audit or free report",
              "For B2B companies in English and Spanish",
              "Service areas: Texas and Florida",
            ]
      }
    >
      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />

        <main>
          {/* HERO */}
          <section className="border-b border-slate-800 bg-slate-950 py-16 lg:py-24">
            <div className="container-custom">
              <div className="grid gap-12 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-center lg:gap-16">
                <div className="min-w-0">
                  <RocSignalLabel index="00" tone="invert">
                    {t.hero.signal}
                  </RocSignalLabel>
                  <h1 className="font-display mt-6 max-w-2xl text-3xl font-bold leading-[1.1] tracking-tight text-white lg:text-5xl">
                    {t.hero.h1}
                  </h1>
                  <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 lg:text-lg">
                    {t.hero.lead}
                  </p>
                  <div className="mt-10 flex flex-wrap items-center gap-6">
                    <a
                      href="#intake"
                      className="group inline-flex items-center gap-3 bg-brand px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand/90"
                    >
                      {t.hero.cta}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </a>
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                      {t.hero.note}
                    </p>
                  </div>
                </div>

                {/* Conceptual flow: NEED → CONTEXT → PRIORITY → NEXT STEP */}
                <div className="min-w-0 border border-slate-800 p-6 lg:p-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    {language === "es" ? "Secuencia de la sesión" : "Session sequence"}
                  </p>
                  <ol className="mt-6 space-y-0">
                    {t.flow.map((step, i) => (
                      <li key={step} className="relative flex items-start gap-5 pb-8 last:pb-0">
                        {i < t.flow.length - 1 && (
                          <span
                            aria-hidden="true"
                            className="absolute left-[7px] top-4 h-full w-px bg-slate-800"
                          />
                        )}
                        <span
                          aria-hidden="true"
                          className="relative mt-1 h-[15px] w-[15px] flex-shrink-0 border border-brand/70"
                        >
                          <span className="absolute inset-[3px] bg-brand" />
                        </span>
                        <span className="min-w-0">
                          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="mt-1 block font-display text-lg font-bold text-white">
                            {step}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </section>

          {/* 01 CONTEXT */}
          <section className="border-b border-border py-14 lg:py-20">
            <div className="container-custom">
              <div className="grid gap-10 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,0.6fr)] lg:gap-16">
                <div>
                  <RocSignalLabel index={s.context.index}>{s.context.label}</RocSignalLabel>
                  <h2 className="font-display mt-5 text-2xl font-bold text-foreground lg:text-3xl">
                    {s.context.title}
                  </h2>
                </div>
                <div className="min-w-0">
                  <p className="text-base leading-relaxed text-muted-foreground">{s.context.body}</p>
                  <ul className="mt-8 border-t border-border">
                    {s.context.points.map((point) => (
                      <li
                        key={point}
                        className="flex gap-4 border-b border-border py-3 text-sm leading-relaxed text-muted-foreground"
                      >
                        <span className="mt-2 h-px w-4 flex-shrink-0 bg-brand" aria-hidden="true" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 02 WHAT WE REVIEW */}
          <section className="border-b border-border py-14 lg:py-20">
            <div className="container-custom">
              <RocSignalLabel index={s.review.index}>{s.review.label}</RocSignalLabel>
              <h2 className="font-display mt-5 max-w-2xl text-2xl font-bold text-foreground lg:text-3xl">
                {s.review.title}
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
                {s.review.body}
              </p>

              <div className="mt-10 grid gap-px border border-border bg-border sm:grid-cols-2">
                {s.review.items.map((item) => (
                  <div key={item.index} className="min-w-0 bg-background p-6 lg:p-8">
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">
                      {item.index}
                    </p>
                    <h3 className="font-display mt-3 text-lg font-bold text-foreground">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 03 WHAT IT IS NOT */}
          <section className="border-b border-slate-800 bg-slate-950 py-14 lg:py-20">
            <div className="container-custom">
              <div className="grid gap-10 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,0.6fr)] lg:gap-16">
                <div>
                  <RocSignalLabel index={s.notIncluded.index} tone="invert">
                    {s.notIncluded.label}
                  </RocSignalLabel>
                  <h2 className="font-display mt-5 text-2xl font-bold text-white lg:text-3xl">
                    {s.notIncluded.title}
                  </h2>
                  <p className="mt-4 text-sm leading-relaxed text-slate-400">{s.notIncluded.body}</p>
                </div>
                <div className="min-w-0">
                  <ul className="border-t border-slate-800">
                    {s.notIncluded.items.map((item) => (
                      <li
                        key={item}
                        className="flex gap-4 border-b border-slate-800 py-3 text-sm leading-relaxed text-slate-300"
                      >
                        <span className="mt-2 h-px w-4 flex-shrink-0 bg-slate-600" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-sm leading-relaxed text-slate-500">{s.notIncluded.note}</p>
                </div>
              </div>
            </div>
          </section>

          {/* 04 FIT */}
          <section className="border-b border-border py-14 lg:py-20">
            <div className="container-custom">
              <RocSignalLabel index={s.fit.index}>{s.fit.label}</RocSignalLabel>
              <h2 className="font-display mt-5 text-2xl font-bold text-foreground lg:text-3xl">
                {s.fit.title}
              </h2>

              <div className="mt-10 grid gap-10 sm:grid-cols-2 lg:gap-16">
                <div>
                  <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">
                    {s.fit.idealTitle}
                  </h3>
                  <ul className="mt-4 border-t border-border">
                    {s.fit.idealItems.map((item) => (
                      <li
                        key={item}
                        className="flex gap-4 border-b border-border py-3 text-sm leading-relaxed text-muted-foreground"
                      >
                        <span className="mt-2 h-px w-4 flex-shrink-0 bg-brand" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    {s.fit.notTitle}
                  </h3>
                  <ul className="mt-4 border-t border-border">
                    {s.fit.notItems.map((item) => (
                      <li
                        key={item}
                        className="flex gap-4 border-b border-border py-3 text-sm leading-relaxed text-muted-foreground"
                      >
                        <span className="mt-2 h-px w-3 flex-shrink-0 bg-border" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 05 HOW IT WORKS */}
          <section className="border-b border-border py-14 lg:py-20">
            <div className="container-custom">
              <RocSignalLabel index={s.how.index}>{s.how.label}</RocSignalLabel>
              <h2 className="font-display mt-5 text-2xl font-bold text-foreground lg:text-3xl">
                {s.how.title}
              </h2>

              <ol className="mt-10 grid gap-px border border-border bg-border md:grid-cols-2 lg:grid-cols-4">
                {s.how.steps.map((step) => (
                  <li key={step.index} className="min-w-0 bg-background p-6 lg:p-8">
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">
                      {step.index}
                    </p>
                    <h3 className="font-display mt-3 text-lg font-bold text-foreground">
                      {step.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* 06 AI SEO RECOMMENDATIONS */}
          <section
            id="recomendaciones-ia"
            className="scroll-mt-24 border-b border-slate-800 bg-slate-950 py-14 lg:py-20"
          >
            <div className="container-custom">
              <RocSignalLabel index="06" tone="invert">
                {language === "es" ? "Recomendaciones con IA" : "AI recommendations"}
              </RocSignalLabel>
              <h2 className="font-display mt-5 max-w-2xl text-2xl font-bold text-white lg:text-3xl">
                {language === "es"
                  ? "Describe tu sitio y recibe una lista priorizada"
                  : "Describe your site and get a prioritized list"}
              </h2>
              <div className="mt-10">
                <AiSeoRecommendations language={language} sourcePage={url} />
              </div>
            </div>
          </section>

          {/* 07 BOOK */}
          <section id="intake" className="scroll-mt-24 border-b border-slate-800 bg-slate-950 py-14 lg:py-20">
            <div className="container-custom">
              <RocSignalLabel index={s.book.index} tone="invert">
                {s.book.label}
              </RocSignalLabel>
              <h2 className="font-display mt-5 text-2xl font-bold text-white lg:text-3xl">
                {s.book.title}
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400">{s.book.body}</p>

              <div className="mt-12">
                <DiagnosisForm language={language} sourcePage={url} />
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="border-b border-border py-14 lg:py-20">
            <div className="container-custom">
              <RocSignalLabel index="08">{t.faq.title}</RocSignalLabel>
              <div className="mt-8 max-w-3xl">
                <Accordion type="single" collapsible className="border-t border-border">
                  {t.faq.items.map((item, index) => (
                    <AccordionItem key={index} value={`faq-${index}`} className="border-b border-border">
                      <AccordionTrigger className="py-5 text-left font-display text-base font-bold hover:no-underline">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="pb-6 text-sm leading-relaxed text-muted-foreground">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </section>

          {/* EXPLORE */}
          <section className="py-14 lg:py-20">
            <div className="container-custom">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                {t.explore.title}
              </p>
              <ul className="mt-6 border-t border-border">
                {t.explore.links.map((link) => (
                  <li key={link.to} className="border-b border-border">
                    <Link
                      to={link.to}
                      className="group flex items-center justify-between gap-4 py-4 text-base font-semibold text-foreground"
                    >
                      <span>{link.label}</span>
                      <ArrowRight className="h-4 w-4 flex-shrink-0 text-brand transition-transform group-hover:translate-x-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </main>

        <Footer language={language} />
        <WhatsAppFloat />
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: t.faq.items.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: { "@type": "Answer", text: item.answer },
            })),
          }),
        }}
      />
    </SEOWrapper>
  );
};

export default DiagnosticoSeoPage;
